import { Box, Button, Dialog, Spinner } from '@veracity/vui'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { DatasetSimple } from '@/interfaces'

import { InputFormControl } from './form'

interface Props {
  isSmall?: boolean
  sourceDataset: DatasetSimple
  allDatasetNames: string[]
  isCloneInProgress: boolean
  onClone: (datasetName: string, onSuccess: () => void) => void
}

interface FormFields {
  name: string
}

export const CloneLibraryDatasetDialog = ({
  isSmall,
  sourceDataset,
  allDatasetNames,
  isCloneInProgress,
  onClone
}: Props) => {
  const [isVisible, setIsVisible] = useState(false)
  const { control, handleSubmit, reset, setValue, formState } =
    useForm<FormFields>()

  const defaultName = sourceDataset.name && `${sourceDataset.name} - Clone`

  useEffect(() => setValue('name', defaultName || ''), [defaultName, setValue])

  const handleClose = () => {
    if (isCloneInProgress) return
    reset({ name: defaultName })
    setIsVisible(false)
  }

  const handleSubmitClick = (formFields: FormFields) =>
    onClone(formFields.name, handleClose)

  const validateNameDuplication = (value?: string) =>
    !allDatasetNames.some(
      p => p.toLocaleLowerCase() === value?.toLocaleLowerCase()
    ) || 'Duplicate dataset name. Please choose a unique name.'

  return (
    <>
      {isSmall ? (
        <Button
          variant="tertiaryDark"
          icon="falCopy"
          onClick={() => setIsVisible(true)}
        />
      ) : (
        <Button
          variant="tertiaryDark"
          iconLeft="falCopy"
          size="lg"
          onClick={() => setIsVisible(true)}>
          Clone
        </Button>
      )}

      <Dialog
        isOpen={isVisible}
        title="Clone Dataset"
        size="lg"
        onClose={handleClose}
        onSubmit={handleSubmit(handleSubmitClick)}
        cancelButton={{ text: 'Cancel', disabled: isCloneInProgress }}
        submitButton={{
          text: 'Clone',
          onClick: handleSubmit(handleSubmitClick),
          isLoading: isCloneInProgress,
          disabled: isCloneInProgress
        }}
        body={
          <Box column p={2} gap={2}>
            {isCloneInProgress ? (
              <Spinner />
            ) : (
              <InputFormControl
                control={control}
                formControlName="name"
                label="Name"
                rules={{ required: true, validate: validateNameDuplication }}
                errorText={
                  formState.errors.name?.type === 'validate'
                    ? 'Provide unique name for dataset'
                    : undefined
                }
              />
            )}
          </Box>
        }
      />
    </>
  )
}
